import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { startOfMonth, subMonths, format, parseISO } from "date-fns";

const PAGE_SIZE = 1000;

export function useDashboardStats() {
  return useQuery({
    queryKey: ["dashboard_stats"],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const [items, cats, pkgs, quotes] = await Promise.all([
        supabase.from("menu_items").select("id", { count: "exact", head: true }).eq("is_active", true),
        supabase.from("menu_categories").select("id", { count: "exact", head: true }),
        supabase.from("packages").select("id", { count: "exact", head: true }).eq("is_active", true),
        supabase.from("quotes").select("id", { count: "exact", head: true }).in("status", ["draft", "sent"]),
      ]);
      return {
        menuItems: items.count ?? 0,
        categories: cats.count ?? 0,
        packages: pkgs.count ?? 0,
        openQuotes: quotes.count ?? 0,
      };
    },
  });
}

export interface MonthlySales {
  month: string;
  label: string;
  total: number;
}

export interface SalesQuote {
  event_date: string;
  total: number;
  amount_paid: number;
  pipeline_stage: string;
}

type SalesClient = Pick<typeof supabase, "from">;

/**
 * Every non-lost quote with an event date, a page at a time (#1233). PostgREST
 * caps a response at max_rows (1000 on prod), so one unpaged request silently
 * drops the rest. Stops only on an empty page, so a max_rows below PAGE_SIZE
 * can't cut the list short either.
 */
export async function fetchAllSalesQuotes(client: SalesClient = supabase): Promise<SalesQuote[]> {
  const all: SalesQuote[] = [];
  let from = 0;
  for (;;) {
    const { data, error } = await client
      .from("quotes")
      .select("event_date, total, amount_paid, pipeline_stage")
      .eq("is_lost", false)
      .not("event_date", "is", null)
      .order("event_date")
      .order("id")
      .range(from, from + PAGE_SIZE - 1);
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as SalesQuote[];
    if (rows.length === 0) return all;
    all.push(...rows);
    from += rows.length;
  }
}

export function useSalesStats() {
  return useQuery({
    queryKey: ["sales_stats"],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const today = format(new Date(), "yyyy-MM-dd");
      const twelveMonthsAgo = format(startOfMonth(subMonths(new Date(), 11)), "yyyy-MM-dd");

      const quotes = await fetchAllSalesQuotes();

      const soldStages = ["deposit_pending", "booked", "won"];
      const futureStages = ["deposit_pending", "booked"];

      const futureSold = quotes
        .filter((q) => q.event_date >= today && futureStages.includes(q.pipeline_stage))
        .reduce((sum, q) => sum + Number(q.total ?? 0), 0);

      const futureDeposits = quotes
        .filter((q) => q.event_date >= today && futureStages.includes(q.pipeline_stage))
        .reduce((sum, q) => sum + Number(q.amount_paid ?? 0), 0);

      const monthlyMap = new Map<string, number>();
      for (let i = 11; i >= 0; i--) {
        const monthStart = startOfMonth(subMonths(new Date(), i));
        const key = format(monthStart, "yyyy-MM");
        monthlyMap.set(key, 0);
      }

      quotes
        .filter((q) => q.event_date >= twelveMonthsAgo && soldStages.includes(q.pipeline_stage))
        .forEach((q) => {
          const monthKey = format(parseISO(q.event_date), "yyyy-MM");
          if (monthlyMap.has(monthKey)) {
            monthlyMap.set(monthKey, (monthlyMap.get(monthKey) ?? 0) + Number(q.total ?? 0));
          }
        });

      const monthlySales: MonthlySales[] = Array.from(monthlyMap.entries()).map(([month, total]) => ({
        month,
        label: format(parseISO(`${month}-01`), "MMM"),
        total,
      }));

      return {
        futureSold,
        futureDeposits,
        monthlySales,
      };
    },
  });
}
